/**
 * The words for an absent record, in one place. Pattern A / RC-11.
 *
 * THE RULE. A missing record is never rendered as a fact. "Nobody wrote a
 * check-in for Nitin on Tuesday" is the only thing the database knows; it does
 * NOT know he was absent. The app said "Absent", in red, on a calendar, in a
 * summary line, in a legend and in an analytics tile — and one surface went
 * further and wrote the inversion out in full: *"No records this month — every
 * working day counts as absent."*
 *
 * WHY IT MATTERS HERE MORE THAN MOST PLACES. These strings sit next to
 * payroll. An attendance percentage coloured red below 75% is a performance
 * claim assembled out of records that were never written, and the person it is
 * about cannot tell the difference between "I did not check in" and "the app
 * did not capture my check-in".
 *
 * WHAT IS PROMOTED. The Field Map already had the correct sentences and was
 * the only surface using them (F305/F389): "Yet to check in today", "No
 * check-in past 10:00 AM IST", "Marked on approved leave today" — note
 * "Marked", which sources the claim. Visit detail had "Not captured." (F339).
 * The Assistant Manager dashboard had `num()` and the `completionPct` null
 * rule. All four are moved here and used everywhere.
 *
 * THE THRESHOLD IS NOT A WORD. "10:00 AM" was a literal inside a sentence.
 * `lateCheckInCopy()` takes the threshold from Settings and refuses to invent
 * one — RC-13, and the same rule as every other Settings value.
 */

/** The absence of a value. Not a fact, not a zero. */
export const NO_VALUE = "—";

/**
 * Attendance vocabulary. The KEY is what the server reports; the VALUE is what
 * a human is allowed to read. `Absent` is still a key because the backend and
 * older rows use it — it is the LABEL that had to change, because the app
 * cannot know it.
 */
export const ATTENDANCE_LABEL = {
  Present: "Present",
  "Half Day": "Half day",
  "On Leave": "On leave",
  Holiday: "Holiday",
  Absent: "Not checked in",
  "Not checked in": "Not checked in",
};

/** No record at all — distinct from every reported status above. */
export const NO_RECORD = "No record";
export const NO_RECORD_HELP =
  "Nothing was captured for this day. This is not a record of absence.";

/** A status string the app does not recognise. Never painted as Present. */
export const UNKNOWN_STATUS = "Status not recognised";

/**
 * The label for a reported attendance status.
 * An unknown status is named as unknown rather than falling through to the
 * first entry in a class map, which is how "Present" green got painted onto
 * anything the server had not been taught to say.
 */
export function attendanceLabel(status) {
  if (status === null || status === undefined || status === "") return NO_RECORD;
  return ATTENDANCE_LABEL[status] || UNKNOWN_STATUS;
}

/** Field-map / team-status wording, promoted from F305/F389. */
export const YET_TO_CHECK_IN = "Yet to check in today";
export const ON_APPROVED_LEAVE = "Marked on approved leave today.";
export const NOT_CAPTURED = "Not captured.";
export const NOT_REPORTED = "Status not reported";
export const NO_VISIT_RECORDED = "No visit recorded today";

/**
 * "No check-in past 10:00 AM IST" — with the hour from Settings.
 *
 * The threshold was hardcoded in the sentence, so a Director changing it in
 * Settings changed the behaviour and not the words. Returns the neutral
 * sentence when the setting has not loaded: a copy string must never be the
 * thing that invents a policy value.
 */
export function lateCheckInCopy(thresholdLabel) {
  if (!thresholdLabel) return "No check-in recorded yet today";
  return `No check-in past ${thresholdLabel} IST`;
}

/**
 * `—`, not `0`, for a value that has not arrived.
 *
 * Promoted verbatim from AssistantManagerDashboard, whose comment is the whole
 * argument: "A stat key this build asks for and an older backend does not send
 * is missing, not zero, and 'Visits Completed 0' is a claim about the day."
 */
export function num(loading, value) {
  return loading || value === undefined || value === null ? NO_VALUE : value;
}

/**
 * A percentage that refuses to exist when either input is missing.
 * `0%` derived from two absent numbers is the more dangerous of the two,
 * because a helper reads as commentary rather than as a value.
 */
export function pct(numerator, denominator) {
  if (numerator === null || numerator === undefined) return null;
  if (!denominator) return null;
  return Math.round((numerator / denominator) * 100);
}

/** Render a percentage, or the dash. Never "0%" from missing inputs. */
export function pctLabel(numerator, denominator) {
  const p = pct(numerator, denominator);
  return p === null ? NO_VALUE : `${p}%`;
}

/**
 * A quantity of DAYS, with the binary-floating-point tail removed.
 *
 * Leave moves in halves, and a PL balance carries two decimals honestly:
 * `leave_policy.pl_summaries` splits a spell that straddles a month as
 * `from_balance * share` and rounds the result to 2dp. Neither 0.13 nor 0.5
 * plus 0.13 is exact in binary, so ADDING TWO DAY FIGURES IN THE BROWSER
 * manufactures a tail the server never sent: the Sales Officer's dashboard
 * summed `CL 8` and `PL 1.13` into a Leave Balance tile reading
 * `9.129999999999999`.
 *
 * Rounds to two places and drops trailing zeros, so 12 still reads "12" and a
 * half day still reads "9.5". Rounding to a WHOLE number would be a different
 * lie in the same place — half days are real, and the suite drives that.
 * `null`/`undefined` pass through as `null` rather than becoming 0 — a balance
 * that did not arrive is not a balance of nothing, and `num()`/`count()`
 * downstream turn that null into the dash.
 *
 * NOT a general number formatter. Money has `formatINR`; this is days.
 */
export function dayCount(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Number(n.toFixed(2));
}

/**
 * A count that distinguishes a real zero from a missing one.
 * `{r.absent || "—"}` collapsed both; a genuine zero must read as `0`.
 */
export function count(value) {
  return value === null || value === undefined ? NO_VALUE : value;
}

/** `1 day` / `2 days` — the `1 days` defect, in one place (RC-14). */
export function plural(n, singular, pluralForm) {
  const one = Number(n) === 1;
  return `${n} ${one ? singular : pluralForm || `${singular}s`}`;
}

/**
 * Join parts with a separator, dropping the empty ones.
 *
 * The leading `, Patna` and trailing ` · ` findings were all the same shape:
 * a template literal with a separator baked in and a value that turned out to
 * be empty. A separator belongs BETWEEN things that exist.
 */
export function joinParts(parts, separator = " · ") {
  return (parts || [])
    .filter((p) => p !== null && p !== undefined && String(p).trim() !== "")
    .join(separator);
}

/**
 * Whether a follow-up (or any due date) is overdue against the IST day.
 * Stored on every visit, read by nothing — an overdue follow-up rendered
 * identically to a future one (RC-11, "overdue follow-ups silent").
 */
export function isOverdue(dueIso, todayKey) {
  if (!dueIso || !todayKey) return false;
  return String(dueIso) < String(todayKey);
}
